/**
 * Local-timezone date helpers (YYYY-MM-DD) without UTC conversion in API params.
 */

/** Format a Date as YYYY-MM-DD in the local timezone (avoids `toISOString()` UTC shift). */
export function toLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse calendar start/end: naive `YYYY-MM-DDTHH:mm[:ss]` as local (session-like); Z/offset as a real instant.
 */
export function parseWallDateTime(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }
  const raw = String(value).trim();
  if (!raw) {
    return new Date(Number.NaN);
  }
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw)) {
    return new Date(raw);
  }
  const [datePart, timePart = '00:00:00'] = raw.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1, hour || 0, minute || 0, second || 0);
}

/**
 * Parse YYYY-MM-DD as local calendar date (avoids UTC shift from `new Date("YYYY-MM-DD")`).
 */
export function parseLocalDateString(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

/**
 * Normalize API/profile date values to YYYY-MM-DD in local calendar.
 */
export function toCalendarDateString(value: Date | string | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return '';
    return toLocalDateString(d);
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    return toLocalDateString(value);
  }
  return '';
}

/** Add days to a YYYY-MM-DD date. Returns undefined if the source is empty/invalid. */
export function addDaysToDateString(
  dateString: string | undefined,
  days: number,
): string | undefined {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
}
